import axiosInstance from "../../components/api/axiosInstance";

const unwrap = (res) => res.data?.data;

/** Same API surface for staff (/dm, all tenants) and tenant portal (/client/dm, own tenant). */
export function createDmApi(base, { isStaff = false } = {}) {
  const get = (url, params) => axiosInstance.get(`${base}${url}`, { params }).then(unwrap);
  const post = (url, body) => axiosInstance.post(`${base}${url}`, body).then(unwrap);
  const put = (url, body) => axiosInstance.put(`${base}${url}`, body).then(unwrap);
  const del = (url) => axiosInstance.delete(`${base}${url}`).then(unwrap);

  return {
    isStaff,
    meta: () => get("/meta"),
    tenants: (search, { all = false, businessTypeId = null, includeInactive = false } = {}) =>
      get("/tenants", {
        search,
        ...(all ? { all: 1 } : {}),
        ...(businessTypeId ? { business_type_id: businessTypeId } : {}),
        ...(includeInactive ? { include_inactive: 1 } : {}),
      }),
    businessTypes: () => get("/business-types"),
    overview: (tenantId) => get("/overview", tenantId ? { tenant_id: tenantId } : undefined),

    listProjects: (params) => get("/projects", params),
    getProject: (id) => get(`/projects/${id}`),
    createProject: (body) => post("/projects", body),
    updateProject: (id, body) => put(`/projects/${id}`, body),
    deleteProject: (id) => del(`/projects/${id}`),
    summary: (id, params) => get(`/projects/${id}/summary`, params),
    projectLeads: (id, params) => get(`/projects/${id}/leads`, params),

    listContent: (projectId, params) => get(`/projects/${projectId}/content`, params),
    createContent: (projectId, body) => post(`/projects/${projectId}/content`, body),
    updateContent: (id, body) => put(`/content/${id}`, body),
    reviewContent: (id, body) => post(`/content/${id}/review`, body),
    deleteContent: (id) => del(`/content/${id}`),

    listPosts: (projectId, params) => get(`/projects/${projectId}/posts`, params),
    createPost: (projectId, body) => post(`/projects/${projectId}/posts`, body),
    updatePost: (id, body) => put(`/posts/${id}`, body),
    deletePost: (id) => del(`/posts/${id}`),

    listCampaigns: (projectId, params) => get(`/projects/${projectId}/campaigns`, params),
    createCampaign: (projectId, body) => post(`/projects/${projectId}/campaigns`, body),
    updateCampaign: (id, body) => put(`/campaigns/${id}`, body),
    deleteCampaign: (id) => del(`/campaigns/${id}`),
    createAd: (campaignId, body) => post(`/campaigns/${campaignId}/ads`, body),
    updateAd: (id, body) => put(`/ads/${id}`, body),
    deleteAd: (id) => del(`/ads/${id}`),

    listMetrics: (projectId, params) => get(`/projects/${projectId}/metrics`, params),
    saveMetrics: (projectId, body) => post(`/projects/${projectId}/metrics`, body),
    deleteMetric: (id) => del(`/metrics/${id}`),
  };
}

export const staffDmApi = createDmApi("/dm", { isStaff: true });
export const portalDmApi = createDmApi("/client/dm");

export function errorMessage(err, fallback = "Something went wrong") {
  return err?.response?.data?.message || err?.response?.data?.error || fallback;
}

export function hasService(access, codes) {
  const services = access?.services || [];
  return codes.some((c) => services.includes(c));
}

const numberFormat = new Intl.NumberFormat("en-IN");
const moneyFormat = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

export const fmtNum = (v) => (v == null || v === "" ? "—" : numberFormat.format(Number(v) || 0));
export const fmtMoney = (v) => (v == null || v === "" ? "—" : moneyFormat.format(Number(v) || 0));
export const fmtDate = (v) => (v ? String(v).slice(0, 10) : "—");

export function toISODate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** [{name, radius_km}] -> "LB Nagar (R2km), BN Reddy" */
export function locationsToText(list) {
  if (!Array.isArray(list)) return "";
  return list
    .map((l) => (l?.radius_km != null ? `${l.name} (R${l.radius_km}km)` : l?.name))
    .filter(Boolean)
    .join(", ");
}

export function listToText(list) {
  return Array.isArray(list) ? list.join(", ") : "";
}

export const nullIfBlank = (v) => (v === "" || v === undefined ? null : v);
export const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
